import {useCallback, useEffect, useState} from "react";
import api from "../..";
import {useBlockUI} from "../../../shared/services/block-ui-service";
import {BaseResponse} from "../../../models/base-response";
import {Programming, ProgrammingAuditLogEntry, ProgrammingFilter, ProgrammingTorreSummary} from "../../../models/programming";
import {ProgrammingLocationDto} from "../../../models/programming-location-dto";

const useProgrammingService = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const blockUI = useBlockUI();

    useEffect(() => {
        blockUI?.setActive(loading);
    }, [loading]);

    const getProgrammings = useCallback(async (filter?: ProgrammingFilter) => {
        try {
            const response = await api.get('/v1/backoffice/programmings', {params: filter});
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const getProgrammingAuditLogs = useCallback(async (id: string) => {
        const response = await api.get<BaseResponse<ProgrammingAuditLogEntry[]>>(
            `/v1/backoffice/programmings/${id}/audit-logs`
        );
        return response?.data?.data ?? [];
    }, []);

    const getProgrammingById = useCallback(async (id: string) => {
        try {
            const response = await api.get<BaseResponse<Programming>>(`/v1/backoffice/programmings/${id}`);
            return response?.data?.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const getProgrammingLocations = useCallback(async (filter?: ProgrammingFilter) => {
        try {
            const response = await api.get<BaseResponse<ProgrammingLocationDto[]>>('/v1/backoffice/programmings/locations', {params: filter});
            return response?.data?.data || [];
        } catch (error) {
            throw error;
        }
    }, []);

    const getProgrammingTorreSummary = useCallback(async (filter?: ProgrammingFilter) => {
        try {
            const response = await api.get<BaseResponse<ProgrammingTorreSummary>>(
                '/v1/backoffice/programmings/torre-summary',
                { params: filter }
            );
            return response?.data?.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const createProgramming = useCallback(async (data: any) => {
        try {
            const response = await api.post('/v1/backoffice/programmings', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const updateProgramming = useCallback(async (id: string, data: any) => {
        try {
            const response = await api.put(`/v1/backoffice/programmings/${id}`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const deleteProgramming = useCallback(async (id: string) => {
        try {
            const response = await api.delete(`/v1/backoffice/programmings/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const cancelProgramming = useCallback(async (id: string, justificationId: string) => {
        try {
            const response = await api.post(`/v1/backoffice/programmings/${id}/cancel`, { justificationId });
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const startProgramming = useCallback(async (id: string) => {
        try {
            const response = await api.post(`/v1/backoffice/programmings/${id}/start`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    const completeProgramming = useCallback(async (id: string) => {
        try {
            const response = await api.post(`/v1/backoffice/programmings/${id}/complete`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    return {
        loading,
        getProgrammings,
        getProgrammingById,
        getProgrammingAuditLogs,
        getProgrammingLocations,
        getProgrammingTorreSummary,
        createProgramming,
        updateProgramming,
        deleteProgramming,
        cancelProgramming,
        startProgramming,
        completeProgramming
    };
};

export default useProgrammingService; 